import { RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Alert } from "@/components/ui/alert";
import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createOrganizationSchema } from "@/lib/schemas/createOrganizationSchema";
import { setApiErrors } from "@/lib/form-utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const generateRandomOrganizationSecret = () => {
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*-_+=";
  let password = "";
  for (let i = 0; i < 32; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    password += characters[randomIndex];
  }
  return password.replace(/^[\s\-\/\t]+|[\s\-\/\t]+$/g, '');
};

interface CreateOrganizationFormValues {
  name: string;
  intactId: string;
  secret: string;
}

interface CreateOrganizationModalProps {
  open: boolean;
  onClose: () => void;
  onCreateOrganization: (
    name: string,
    intactId: string,
    secret: string,
  ) => Promise<void>;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = ({
  open,
  onClose,
  onCreateOrganization,
}) => {
  const form = useForm<CreateOrganizationFormValues>({
    defaultValues: {
      name: "",
      intactId: "",
      secret: generateRandomOrganizationSecret(),
    },
    resolver: zodResolver(createOrganizationSchema),
  });

  const onSubmit = async (data: CreateOrganizationFormValues) => {
    try {
      await onCreateOrganization(data.name, data.intactId, data.secret);
      onClose();
      form.reset({ name: "", intactId: "", secret: generateRandomOrganizationSecret() });
    } catch (error) {
      const rootMsg = setApiErrors(error, form.setError, {
        Name: "name",
        OrganizationSecret: "secret",
      });
      if (rootMsg) {
        form.setError("root", { message: rootMsg });
      }
    }
  };

  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      form.reset({ name: "", intactId: "", secret: generateRandomOrganizationSecret() });
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
    >
      <DialogContent className="min-w-[30%] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Create new organization
          </DialogTitle>
        </DialogHeader>
        <Separator />
        {form.formState.errors.root && (
          <Alert variant="destructive">
            {form.formState.errors.root.message}
          </Alert>
        )}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-2 mb-1">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="intactId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Intact ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="secret"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Secret</FormLabel>
                  <div className="relative flex items-center">
                    <FormControl>
                      <Input
                        {...field}
                        disabled={form.formState.isSubmitting}
                        className="pr-10"
                      />
                    </FormControl>
                    <div className="absolute right-1">
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        type="button"
                        onClick={() => form.setValue("secret", generateRandomOrganizationSecret())}
                        disabled={form.formState.isSubmitting}
                      >
                        <RefreshCw className="size-4" />
                      </Button>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Separator />
            <DialogFooter>
              <Button
                variant="secondary"
                type="button"
                onClick={handleClose}
                disabled={form.formState.isSubmitting}
                className="w-20"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="w-20"
              >
                {form.formState.isSubmitting ? "..." : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateOrganizationModal;
